<!--
  CustomerContextPanel — Phase 4-6 / T30.

  Right-rail panel showing what the operator knows about the customer in the
  current conversation. Two states (per the host-summary contract):

    MATCHED   — squircle monogram + name + match pill + badges, stacked
                key/value fields (label uppercase muted, value plain), an
                optional hairline-rail timeline, "Open in Host" ghost CTA,
                and a LinkedEntitiesList footer.
    UNMATCHED — dashed-border empty state with the external sender ref shown
                as a mono caption. No CTA; manual-match search lands in v1.5.

  Layout direction set by impeccable (see batch report). No outer card; the
  panel sits flush with a left hairline + subtle 2.5% surface tint to separate
  it from the thread without becoming a heavy SaaS sidebar.

  Data source: hostSummary(conversationId) queryOptions from src/lib/api/queries.ts.
  Outbound: openHostEntity via the bridge singleton (lib/embed/bridge-singleton).
-->
<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import {
    customerProfile,
    hostSummary,
    type Conversation,
    type CustomerProfile,
    type HostSummary,
    type HostSummaryActivity,
    type HostSummaryField,
  } from '../lib/api/queries.js';
  import { postOpenHostEntity } from '../lib/embed/bridge-singleton.svelte.js';
  import { session } from '../lib/stores/session.svelte.js';
  import LinkedEntitiesList from './LinkedEntitiesList.svelte';
  import NotesPanel from './NotesPanel.svelte';
  import TagPicker from './TagPicker.svelte';
  import TagManageModal from './TagManageModal.svelte';
  import AssigneePicker from './AssigneePicker.svelte';

  type Props = {
    conversationId: string;
    conversation?: Conversation | null;
  };
  let { conversationId, conversation = null }: Props = $props();

  // Right-panel reshuffle — tags + notes moved out of ConversationHeader
  // and ThreadPage into this panel. The picker mounts inside this
  // component's tags-anchor; the manage modal mounts at component root
  // so its fixed-position backdrop covers the whole viewport regardless
  // of the panel's own scroll.
  const appliedTags = $derived(conversation?.appliedTags ?? []);
  let tagPickerOpen = $state(false);
  let tagManageOpen = $state(false);

  function onTagsTriggerClick(event: MouseEvent) {
    event.stopPropagation();
    tagPickerOpen = !tagPickerOpen;
  }
  function closeTagPicker() {
    tagPickerOpen = false;
  }
  function openTagManage() {
    tagPickerOpen = false;
    tagManageOpen = true;
  }
  function closeTagManage() {
    tagManageOpen = false;
  }

  // Bound to the Tags trigger so TagPicker can position itself relative
  // to it via getBoundingClientRect (picker is position:fixed and would
  // otherwise be clipped by the panel's overflow-y: auto).
  let tagsTriggerEl: HTMLButtonElement | null = $state(null);

  // ---- G4.3c — assignee wiring (mirror of admin panel) ------------------
  const assignee = $derived(conversation?.assignee ?? null);
  const assigneeTenantId = $derived(conversation?.tenantId ?? null);
  let assigneePickerOpen = $state(false);
  let assigneeTriggerEl: HTMLButtonElement | null = $state(null);

  function onAssigneeTriggerClick(event: MouseEvent) {
    event.stopPropagation();
    assigneePickerOpen = !assigneePickerOpen;
  }
  function closeAssigneePicker() {
    assigneePickerOpen = false;
  }
  /**
   * Two-letter initials for the chip avatar — matches AssigneePicker +
   * row chip so the same operator renders identically across surfaces.
   */
  function assigneeInitials(name: string | null): string {
    const trimmed = (name ?? '').trim();
    if (trimmed.length === 0) return '?';
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }

  function tagBg(color: string): string {
    return `color-mix(in oklch, ${color} 22%, transparent)`;
  }
  function tagFg(color: string): string {
    return `color-mix(in oklch, ${color} 70%, black)`;
  }
  function tagBorder(color: string): string {
    return `color-mix(in oklch, ${color} 40%, transparent)`;
  }

  // Meta-side profile: always rendered when available. Independent of the
  // host-summary query so the panel never blanks on host-adapter failure.
  const meta = $derived(createQuery(customerProfile(conversationId)));
  const metaData = $derived<CustomerProfile | undefined>($meta.data);
  let avatarFailed = $state(false);
  const showAvatar = $derived(Boolean(metaData?.profilePicUrl) && !avatarFailed);
  const metaName = $derived(
    metaData?.displayName?.trim() ||
      [metaData?.firstName, metaData?.lastName].filter(Boolean).join(' ').trim() ||
      null,
  );
  const metaHue = $derived(hashHue(metaName ?? metaData?.externalSenderRef ?? conversationId));
  const metaInitials = $derived(extractInitials(metaName));

  // Meta only returns first/last name when the app holds Advanced Access to
  // the messaging permission AND the customer's privacy allows it — for many
  // pages it comes back empty. When both are absent we collapse the two bare
  // "—" rows into a single honest line instead.
  const profileNameMissing = $derived(
    Boolean(metaData) && !metaData?.firstName && !metaData?.lastName,
  );
  const profileSourceLabel = $derived(
    metaData?.channelType ? channelLabel(metaData.channelType) : 'the customer',
  );

  const q = $derived(createQuery(hostSummary(conversationId)));

  const data = $derived<HostSummary | undefined>($q.data);
  const isMatched = $derived(
    Boolean(data?.summary) && data?.matchStatus !== 'UNMATCHED' && data?.matchStatus !== 'DECLINED',
  );

  // Derive a deterministic monogram hue + initials from the customer id.
  const monogramSource = $derived(
    data?.summary?.displayName?.trim() || conversation?.customerId || conversationId,
  );
  const hue = $derived(hashHue(monogramSource));
  const initials = $derived(extractInitials(data?.summary?.displayName ?? null));

  const hostProductLabel = $derived(
    session.state.sso?.tenant?.name?.trim() || 'host product',
  );

  // Find the first openable linked entity for the "Open in <Host>" CTA. For v1
  // we treat the first linked entity with a deepLink as the canonical customer
  // record — adapters wire customer first when matched.
  const primaryOpenable = $derived(
    (data?.linkedEntities ?? []).find((e) => Boolean(e.deepLink)) ?? null,
  );

  function channelLabel(channelType: string): string {
    switch (channelType) {
      case 'META':
        return 'Facebook';
      case 'META_INSTAGRAM':
        return 'Instagram';
      case 'WHATSAPP':
        return 'WhatsApp';
      default:
        return channelType;
    }
  }

  function hashHue(input: string): number {
    let h = 0;
    for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
  }

  function extractInitials(n: string | null): string {
    if (!n) return '·';
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '·';
    const first = parts[0]?.[0] ?? '';
    const second = parts[1]?.[0] ?? '';
    return (first + second).toUpperCase() || '·';
  }

  function formatActivityTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const diff = Math.max(0, Date.now() - then);
    const min = 60_000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (diff < min) return 'just now';
    if (diff < hr) return `${Math.floor(diff / min)}m ago`;
    if (diff < day) return `${Math.floor(diff / hr)}h ago`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function matchLabel(): string {
    switch (data?.matchStatus) {
      case 'AUTO':
        return 'Matched · auto';
      case 'MANUAL':
        return 'Matched · by operator';
      default:
        return '';
    }
  }

  function openInHost() {
    if (!primaryOpenable) return;
    postOpenHostEntity(primaryOpenable.entityType, primaryOpenable.entityId);
  }

</script>

<aside class="panel" aria-label="Customer context">
  <section class="meta">
    {#if $meta.isPending && !metaData}
      <div class="status">Loading customer...</div>
    {:else}
      <div class="meta-ident">
        {#if showAvatar}
          <img
            class="meta-avatar"
            src={metaData?.profilePicUrl ?? ''}
            alt=""
            referrerpolicy="no-referrer"
            onerror={() => (avatarFailed = true)}
          />
        {:else}
          <div
            class="meta-avatar meta-mono"
            style="--mono-bg: oklch(88% 0.05 {metaHue}); --mono-fg: oklch(28% 0.12 {metaHue})"
            aria-hidden="true"
          >
            {metaInitials}
          </div>
        {/if}
        <div class="meta-name" title={metaName ?? ''}>{metaName ?? 'Customer'}</div>
      </div>
      <div class="meta-fields">
        {#if profileNameMissing}
          <div class="field">
            <div class="field-label">Name</div>
            <div class="field-value field-empty">Not shared by {profileSourceLabel}</div>
          </div>
        {:else}
          <div class="field">
            <div class="field-label">First name</div>
            <div class="field-value">{metaData?.firstName ?? '—'}</div>
          </div>
          <div class="field">
            <div class="field-label">Last name</div>
            <div class="field-value">{metaData?.lastName ?? '—'}</div>
          </div>
        {/if}
        {#if metaData?.phoneNumber}
          <div class="field">
            <div class="field-label">Phone</div>
            <div class="field-value">
              <a class="phone-link" href={`tel:${metaData.phoneNumber}`}>{metaData.phoneNumber}</a>
            </div>
          </div>
        {/if}
        {#if metaData?.channelType}
          <div class="field">
            <div class="field-label">Channel</div>
            <div class="field-value">
              <div class="channel-line">
                <span class="channel-name">{channelLabel(metaData.channelType)}</span>
                {#if metaData.pageName}
                  <span class="channel-sep" aria-hidden="true"> · </span>
                  <span class="channel-page">{metaData.pageName}</span>
                {/if}
              </div>
              {#if metaData.externalPageId}
                <code class="channel-ref">{metaData.externalPageId}</code>
              {/if}
            </div>
          </div>
        {/if}
      </div>
      {#if metaData?.externalSenderRef}
        <div class="ref">
          <div class="ref-label">External sender ref</div>
          <code class="ref-value" title={metaData.externalSenderRef}>{metaData.externalSenderRef}</code>
        </div>
      {/if}
    {/if}
  </section>

  {#if $q.isPending}
    <div class="status host-status">Loading host record...</div>
  {:else if $q.error}
    <div class="status error host-status">
      <span>Could not load host record.</span>
      <button type="button" class="link" onclick={() => $q.refetch()}>Try again</button>
    </div>
  {:else if isMatched && data?.summary}
    {@const fields = data.summary.fields ?? []}
    {@const badges = (data.summary.badges ?? []).slice(0, 3)}
    {@const activity = data.summary.recentActivity ?? []}
    <section class="head">
      <div class="ident">
        <div
          class="mono"
          style="--mono-bg: oklch(88% 0.05 {hue}); --mono-fg: oklch(28% 0.12 {hue})"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div class="name-block">
          <div class="name" title={data.summary.displayName}>{data.summary.displayName}</div>
          {#if matchLabel()}
            <div class="match-pill">
              <span class="dot" aria-hidden="true"></span>
              <span>{matchLabel()}</span>
            </div>
          {/if}
        </div>
      </div>
      {#if badges.length > 0}
        <div class="badges" role="list">
          {#each badges as badge, i (badge + i)}
            {#if i > 0}<span class="badge-sep" aria-hidden="true">·</span>{/if}
            <span class="badge" class:badge-accent={i === 0} role="listitem">{badge}</span>
          {/each}
        </div>
      {/if}
    </section>

    {#if fields.length > 0}
      <section class="fields">
        {#each fields as field, i (field.label + i)}
          {@const f = field as HostSummaryField}
          <div class="field">
            <div class="field-label">{f.label}</div>
            <div class="field-value">{f.value}</div>
          </div>
        {/each}
      </section>
    {/if}

    {#if activity.length > 0}
      <section class="activity">
        <h3 class="section-h">Recent activity</h3>
        <ol class="rail">
          {#each activity.slice(0, 4) as item, i (item.at + i)}
            {@const a = item as HostSummaryActivity}
            <li class="rail-item">
              <span class="rail-node" aria-hidden="true"></span>
              <span class="rail-time">{formatActivityTime(a.at)}</span>
              <span class="rail-text">{a.text}</span>
            </li>
          {/each}
        </ol>
      </section>
    {/if}

    {#if primaryOpenable}
      <div class="cta-row">
        <button type="button" class="cta" onclick={openInHost}>
          Open in {hostProductLabel}
          <span class="cta-glyph" aria-hidden="true">↗</span>
        </button>
      </div>
    {/if}

    <LinkedEntitiesList linkedEntities={data.linkedEntities ?? []} />
  {:else}
    <section class="unmatched">
      <div class="dashed">
        <p class="heading">Unmatched contact</p>
        <p class="sub">We don't have a host record linked to this customer yet.</p>
      </div>
      <LinkedEntitiesList linkedEntities={data?.linkedEntities ?? []} />
    </section>
  {/if}

  <!-- G4.3c — Assignee section. Sits above Tags so the most actionable
       field is closest to the customer block. Mirrors tags-section's
       header pattern (chip below, picker anchored to the button). -->
  <section class="side-section assignee-section" aria-label="Assignee">
    <header class="side-section-header">
      <strong>Assigned to</strong>
      <div class="assignee-anchor">
        <button
          bind:this={assigneeTriggerEl}
          type="button"
          class="side-action"
          aria-haspopup="dialog"
          aria-expanded={assigneePickerOpen}
          aria-label="Change conversation assignee"
          disabled={!assigneeTenantId}
          onclick={onAssigneeTriggerClick}
        >
          {assignee ? 'Change' : 'Assign'}
        </button>
        {#if assigneePickerOpen && assigneeTenantId}
          <AssigneePicker
            {conversationId}
            tenantId={assigneeTenantId}
            currentAssigneeUserId={assignee?.userId ?? null}
            anchor={assigneeTriggerEl}
            onClose={closeAssigneePicker}
          />
        {/if}
      </div>
    </header>
    {#if assignee}
      <div class="assignee-current" aria-label="Current assignee">
        <span class="assignee-avatar" aria-hidden="true">{assigneeInitials(assignee.displayName)}</span>
        <span class="assignee-name">{assignee.displayName ?? 'Operator'}</span>
      </div>
    {:else}
      <p class="side-section-empty">Unassigned.</p>
    {/if}
  </section>

  <section class="side-section tags-section" aria-label="Tags">
    <header class="side-section-header">
      <strong>Tags</strong>
      <div class="tags-anchor">
        <button
          bind:this={tagsTriggerEl}
          type="button"
          class="side-action"
          aria-haspopup="dialog"
          aria-expanded={tagPickerOpen}
          aria-label="Edit conversation tags"
          onclick={onTagsTriggerClick}
        >
          {appliedTags.length > 0 ? 'Edit' : 'Add tag'}
        </button>
        {#if tagPickerOpen}
          <TagPicker
            {conversationId}
            {appliedTags}
            anchor={tagsTriggerEl}
            onClose={closeTagPicker}
            onManage={openTagManage}
          />
        {/if}
      </div>
    </header>
    {#if appliedTags.length > 0}
      <div class="applied-strip" aria-label="Applied tags">
        {#each appliedTags as t (t.id)}
          <span
            class="applied-chip"
            title={t.name}
            style="background-color: {tagBg(t.color)}; color: {tagFg(t.color)}; border-color: {tagBorder(t.color)};"
          >{t.name}</span>
        {/each}
      </div>
    {:else}
      <p class="side-section-empty">No tags applied.</p>
    {/if}
  </section>

  <NotesPanel {conversationId} />
</aside>

{#if tagManageOpen}
  <TagManageModal onClose={closeTagManage} />
{/if}

<style>
  /* Right-panel reshuffle — Tags + Notes sections appended after the
     existing matched/unmatched blocks. Each section has a uppercase
     micro-label header matching the panel's existing field-label tone. */
  .side-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px 0;
    border-top: 1px solid var(--widget-divider, oklch(93% 0.006 250));
  }
  .side-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .side-section-header strong {
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
  }
  /* G4.3c — assignee anchor uses the same positioning pattern as tags. */
  .tags-anchor,
  .assignee-anchor {
    position: relative;
  }
  .side-action {
    background: none;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    padding: 3px 9px;
    font-family: var(--widget-font);
    font-size: 11.5px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
    cursor: pointer;
  }
  .side-action:hover:not(:disabled) {
    border-color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }
  .side-action:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
  /* G4.3c — disabled while we wait for the conversation's tenantId to
     load. Without this, clicking Assign before the row resolves would
     open a picker that can't fetch candidates. */
  .side-action:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .assignee-current {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .assignee-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .assignee-name {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .side-section-empty {
    margin: 0;
    font-size: 12px;
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
    font-style: italic;
  }
  .applied-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .applied-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .panel {
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    border-left: 1px solid var(--widget-border, oklch(92% 0.005 250));
    background: color-mix(
      in oklch,
      var(--widget-fg, oklch(20% 0.01 250)) 2.5%,
      var(--widget-bg, oklch(99% 0.003 250))
    );
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-size: 13px;
    line-height: 1.45;
  }

  .status {
    padding: 20px 16px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 12.5px;
    text-align: center;
  }
  .status.error {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .host-status {
    border-top: 1px solid color-mix(in oklch, var(--widget-border, oklch(92% 0.005 250)) 60%, transparent);
    padding: 14px 16px;
  }

  .meta {
    padding: 16px 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .meta-ident {
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 12px;
    align-items: center;
  }
  .meta-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
    display: block;
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 6%, transparent);
  }
  .meta-mono {
    background: var(--mono-bg);
    color: var(--mono-fg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .meta-name {
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .meta-fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .channel-line {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px;
  }
  .channel-name,
  .channel-page {
    font-weight: 500;
  }
  .channel-sep {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .channel-ref {
    display: inline-block;
    margin-top: 2px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .phone-link {
    color: var(--widget-accent, oklch(75% 0.165 75));
    text-decoration: none;
    font-weight: 500;
  }
  .phone-link:hover {
    text-decoration: underline;
  }
  .meta .ref {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .link {
    background: none;
    border: none;
    color: var(--widget-accent, oklch(75% 0.165 75));
    font-weight: 600;
    font-size: inherit;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
  }

  .head {
    padding: 16px 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ident {
    display: grid;
    grid-template-columns: 40px 1fr;
    gap: 12px;
    align-items: center;
  }

  .mono {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--mono-bg);
    color: var(--mono-fg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .name-block {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .name {
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .match-pill {
    font-size: 10.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .match-pill .dot {
    width: 5px;
    height: 5px;
    border-radius: 999px;
    background: oklch(62% 0.16 145);
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    padding-left: 52px;
  }
  .badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .badge-accent {
    color: var(--widget-accent, oklch(75% 0.165 75));
  }
  .badge-sep {
    color: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 25%, transparent);
    font-size: 10px;
  }

  .fields {
    padding: 6px 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-top: 1px solid color-mix(in oklch, var(--widget-border, oklch(92% 0.005 250)) 60%, transparent);
    padding-top: 14px;
    margin-top: 2px;
  }
  .field-label {
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 3px;
  }
  .field-value {
    font-size: 13px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    word-break: break-word;
    line-height: 1.35;
  }
  /* Shown when Meta returns no profile name — reads as "no data", not a value. */
  .field-empty {
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
    font-style: italic;
  }

  .activity {
    padding: 16px 16px 14px;
    border-top: 1px solid color-mix(in oklch, var(--widget-border, oklch(92% 0.005 250)) 60%, transparent);
  }
  .section-h {
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 700;
    margin: 0 0 12px;
  }
  .rail {
    list-style: none;
    padding: 0;
    margin: 0;
    position: relative;
  }
  .rail::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 8px;
    bottom: 8px;
    width: 1px;
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 12%, transparent);
  }
  .rail-item {
    display: grid;
    grid-template-columns: 9px 1fr;
    column-gap: 12px;
    padding: 0 0 14px;
    position: relative;
  }
  .rail-item:last-child {
    padding-bottom: 0;
  }
  .rail-node {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: var(--widget-accent, oklch(75% 0.165 75));
    margin-top: 4px;
    grid-row: span 2;
    align-self: start;
    box-shadow: 0 0 0 3px var(--widget-bg, oklch(99% 0.003 250));
  }
  .rail-time {
    grid-column: 2;
    font-size: 10.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 3px;
  }
  .rail-text {
    grid-column: 2;
    font-size: 12.5px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    line-height: 1.4;
  }

  .cta-row {
    padding: 14px 16px 16px;
    border-top: 1px solid color-mix(in oklch, var(--widget-border, oklch(92% 0.005 250)) 60%, transparent);
  }
  .cta {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 12px;
    background: transparent;
    color: var(--widget-accent, oklch(75% 0.165 75));
    border: 1px solid var(--widget-accent, oklch(75% 0.165 75));
    border-radius: var(--widget-radius-action, 5px);
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .cta:hover {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 8%, transparent);
  }
  .cta:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .cta-glyph {
    font-size: 12px;
    line-height: 1;
  }

  .unmatched {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .dashed {
    border: 1px dashed color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 22%, transparent);
    border-radius: var(--widget-radius, 8px);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .heading {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .sub {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .ref {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .ref-label {
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
  }
  .ref-value {
    display: block;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11.5px;
    padding: 8px 10px;
    border-radius: 8px;
    background: color-mix(
      in oklch,
      var(--widget-fg, oklch(20% 0.01 250)) 5%,
      var(--widget-bg, oklch(99% 0.003 250))
    );
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow-wrap: anywhere;
    user-select: all;
  }
</style>
